// Declaratie — 8-status state-machine voor module C4 (Declaraties).
//
// Bron-hiërarchie:
//   - State-table uit `packages/utils/src/declaraties-calculations.ts`
//     (`DECLARATIE_STATE_TRANSITIONS`).
//   - Role-rules uit `apps/api/src/routes/declaraties.ts`
//     (HR-only voor HR-review, HR/SALARIS/ADMIN voor uitbetalen).
//
// Workflow:
//   CONCEPT → INGEDIEND → GOEDGEKEURD_TEAMLEIDER → GOEDGEKEURD_HR → UITBETAALD
//                       ↘ AFGEKEURD_TEAMLEIDER → CONCEPT  (her-bewerken)
//                       ↘ CONCEPT  (withdraw)
//                                         ↘ AFGEKEURD_HR → CONCEPT
//                                         ↘ CONCEPT  (withdraw)
//   * → INGETROKKEN  (eindstaat — concept weggooien of na review intrekken).

import { WorkflowEngine, type WorkflowDefinition } from '../workflow-engine'

/** Mirror van Prisma DeclaratieStatus enum als string-literal union. */
export type DeclaratieWorkflowStatusValue =
  | 'CONCEPT'
  | 'INGEDIEND'
  | 'GOEDGEKEURD_TEAMLEIDER'
  | 'AFGEKEURD_TEAMLEIDER'
  | 'GOEDGEKEURD_HR'
  | 'AFGEKEURD_HR'
  | 'UITBETAALD'
  | 'INGETROKKEN'
  | 'AANVULLENDE_VRAAG'

export const declaratieWorkflow: WorkflowDefinition<DeclaratieWorkflowStatusValue> = {
  name: 'declaratie',
  initial: 'CONCEPT',
  states: [
    'CONCEPT',
    'INGEDIEND',
    'GOEDGEKEURD_TEAMLEIDER',
    'AFGEKEURD_TEAMLEIDER',
    'GOEDGEKEURD_HR',
    'AFGEKEURD_HR',
    'UITBETAALD',
    'INGETROKKEN',
    'AANVULLENDE_VRAAG',
  ],
  transitions: [
    // 1. Indienen
    {
      from: 'CONCEPT',
      to: 'INGEDIEND',
      label: 'Indienen',
      rolesAllowed: ['MONTEUR', 'TEAMLEIDER', 'PLANNER', 'HR', 'SALARIS', 'MANAGER', 'ADMIN'],
    },
    // 2. TL beoordelen
    {
      from: 'INGEDIEND',
      to: 'GOEDGEKEURD_TEAMLEIDER',
      label: 'TL goedkeuren',
      rolesAllowed: ['TEAMLEIDER', 'HR', 'ADMIN'],
    },
    {
      from: 'INGEDIEND',
      to: 'AFGEKEURD_TEAMLEIDER',
      label: 'TL afkeuren',
      rolesAllowed: ['TEAMLEIDER', 'HR', 'ADMIN'],
    },
    // 3. Withdraw uit ingediend / TL-goedgekeurd terug naar concept
    {
      from: 'INGEDIEND',
      to: 'CONCEPT',
      label: 'Terugtrekken naar concept',
    },
    {
      from: 'GOEDGEKEURD_TEAMLEIDER',
      to: 'CONCEPT',
      label: 'Terugtrekken naar concept',
    },
    // 4. HR review
    {
      from: 'GOEDGEKEURD_TEAMLEIDER',
      to: 'GOEDGEKEURD_HR',
      label: 'HR goedkeuren',
      rolesAllowed: ['HR', 'ADMIN'],
    },
    {
      from: 'GOEDGEKEURD_TEAMLEIDER',
      to: 'AFGEKEURD_HR',
      label: 'HR afkeuren',
      rolesAllowed: ['HR', 'ADMIN'],
    },
    // 5. Uitbetalen
    {
      from: 'GOEDGEKEURD_HR',
      to: 'UITBETAALD',
      label: 'Markeren als uitbetaald',
      rolesAllowed: ['HR', 'SALARIS', 'ADMIN'],
    },
    // 6. Her-bewerken na afkeur
    {
      from: 'AFGEKEURD_TEAMLEIDER',
      to: 'CONCEPT',
      label: 'Bewerken na TL-afkeur',
    },
    {
      from: 'AFGEKEURD_HR',
      to: 'CONCEPT',
      label: 'Bewerken na HR-afkeur',
    },
    // 7. Intrekken vanuit eerstelijns-statussen
    {
      from: 'CONCEPT',
      to: 'INGETROKKEN',
      label: 'Intrekken',
    },
    {
      from: 'INGEDIEND',
      to: 'INGETROKKEN',
      label: 'Intrekken',
    },
    {
      from: 'GOEDGEKEURD_TEAMLEIDER',
      to: 'INGETROKKEN',
      label: 'Intrekken',
    },
    // 8. FO-055 (batch-17) P2 — aanvullende vraag (zonder goed/af te keuren). Een
    // beoordelaar kan in de TL- of HR-fase een vraag stellen; de declaratie staat
    // dan tijdelijk bij de medewerker (AANVULLENDE_VRAAG) en keert na het antwoord
    // terug naar INGEDIEND in de beoordeel-inbox. De rol-gate zit op de endpoints
    // (canApprove voor de vraag, alleen de aanvrager voor het antwoord).
    {
      from: 'INGEDIEND',
      to: 'AANVULLENDE_VRAAG',
      label: 'Aanvullende vraag stellen',
    },
    {
      from: 'GOEDGEKEURD_TEAMLEIDER',
      to: 'AANVULLENDE_VRAAG',
      label: 'Aanvullende vraag stellen',
    },
    {
      from: 'AANVULLENDE_VRAAG',
      to: 'INGEDIEND',
      label: 'Aanvullende vraag beantwoord',
    },
  ],
}

/** Engine-instance voor declaraties — direct importeerbaar in API-routes. */
export const declaratieEngine = new WorkflowEngine(declaratieWorkflow)
